import { useState, useEffect } from "react";
const secure: boolean = process.env.REACT_APP_SECURE === "true";
const sameSite: string = 'Strict'
const fullUrl = process.env.REACT_APP_SERVER_URL
let domain: string = '';
if (fullUrl) {
    let hostname = new URL(fullUrl).hostname;
    if (hostname.startsWith('api.')) {
        domain = hostname.slice(4);
    } else if (hostname.startsWith('apiqa.')) {
        domain = hostname.slice(6);
    } else if (hostname.startsWith('apiprod.')) {
        domain = hostname.slice(8);
    } else {
        domain = hostname;
    }
}

interface AuthState {
    userName: string;
    accessToken: string;
    role: string;
    dealerId: string;
    dealershipName: string;
    dealerCode: string;
    email: string;
}

const getCookie = (name: string) => {
    const cookie = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${name}=`));

    return cookie?.split('=')[1];
}

const setCookie = (name: string, value: string) => {
    document.cookie = `${name}=${value}; domain=${domain}; ${secure ? 'secure;' : ''} SameSite=${sameSite}`;
}

const dispatchCookieChangeEvent = () => {
    window.dispatchEvent(new CustomEvent('cookieChange'));
}

export default function useToken() {
    const [auth, setAuth] = useState<AuthState>({} as AuthState);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const accessToken = getCookie('accessToken');
        const userName = getCookie('userName');
        const role = getCookie('role');
        const dealerId = getCookie('dealerId');
        const dealershipName = getCookie('dealershipName');
        const dealerCode = getCookie('dealerCode');
        const email = getCookie('email');

        if (accessToken && userName) {
            setAuth({
                accessToken,
                userName,
                role: role || '',
                dealerId: dealerId || '',
                dealershipName: dealershipName || '',
                dealerCode: dealerCode || '',
                email: email || ''
            });
            setIsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
        }
        setIsLoading(false);
    }, []);

    const logIn = (auth: AuthState) => {
        setAuth(auth);
        setCookie('userName', auth.userName);
        setCookie('accessToken', auth.accessToken);
        setCookie('role', auth.role);
        setCookie('dealerId', auth.dealerId);
        setCookie('dealershipName', auth.dealershipName)
        setCookie('dealerCode', auth.dealerCode);
        setCookie('email', auth.email);
        setIsAuthenticated(true);
        dispatchCookieChangeEvent();
    }

    const deleteCookie = (name: string) => {
        document.cookie = `${name} =; expires= Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/; ${secure ? 'secure;' : ''} SameSite=${sameSite}`;
    }

    const logOut = () => {
        setAuth({} as AuthState);
        deleteCookie("userName");
        deleteCookie("accessToken");
        deleteCookie("role");
        deleteCookie("dealerId");
        deleteCookie("dealerCode");

        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && !key.startsWith("grid_filters") && !key.startsWith("PrintManager")) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));

        sessionStorage.clear();
        setIsAuthenticated(false);
        dispatchCookieChangeEvent();
        window.location.href = '/';
    }

    const validateToken = () => {
        const accessToken = getCookie('accessToken');
        if (!accessToken) {
            setIsAuthenticated(false);
            throw new Error('No access token found. Please log in.');
        }
        return accessToken;
    }

    return {
        auth,
        isAuthenticated,
        isLoading,
        logIn,
        logOut,
        validateToken
    }
}